import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings } from 'lucide-react';
import { Button } from './ui/button';

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be changed
    analytics: false,
    functional: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Delay showing banner for better UX
      setTimeout(() => setShow(true), 1000);
    }
  }, []);

  const savePreferences = (acceptAll = false) => {
    const prefs = acceptAll 
      ? { essential: true, analytics: true, functional: true }
      : preferences;
    
    localStorage.setItem('cookie_consent', JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString()
    }));
    setShow(false);
  };

  const rejectAll = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      essential: true,
      analytics: false,
      functional: false,
      timestamp: new Date().toISOString()
    }));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 shadow-2xl" data-testid="cookie-consent-banner">
      <div className="max-w-7xl mx-auto">
        {!showSettings ? (
          // Main banner
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Cookie className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">We Value Your Privacy</h3>
                <p className="text-gray-300 text-sm mt-1 max-w-2xl">
                  We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies. 
                  <Link to="/cookie-policy" className="text-blue-400 hover:text-blue-300 ml-1">Learn more</Link>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 ml-16 md:ml-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                <Settings className="h-4 w-4 mr-2" />
                Customize
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={rejectAll}
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                Reject All
              </Button>
              <Button
                size="sm"
                onClick={() => savePreferences(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="accept-cookies-btn"
              >
                Accept All
              </Button>
            </div>
          </div>
        ) : (
          // Settings panel
          <div className="relative">
            <button
              onClick={() => setShowSettings(false)}
              className="absolute top-0 right-0 text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            
            <h3 className="text-white font-semibold text-lg mb-4">Cookie Preferences</h3>
            
            <div className="space-y-4 mb-6">
              {/* Essential Cookies */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <h4 className="text-white font-medium">Essential Cookies</h4>
                  <p className="text-gray-400 text-sm">Required for the website to function. Cannot be disabled.</p>
                </div>
                <div className="text-green-400 text-sm font-medium">Always On</div>
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <h4 className="text-white font-medium">Analytics Cookies</h4>
                  <p className="text-gray-400 text-sm">Help us understand how visitors use our website.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {/* Functional Cookies */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <h4 className="text-white font-medium">Functional Cookies</h4>
                  <p className="text-gray-400 text-sm">Enable enhanced functionality and personalization.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(false)}
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => savePreferences(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
